import React, { useState, useEffect } from 'react';
import { Box, Typography, Card, CardContent, Switch, FormControlLabel, Button, Divider, Grid, Chip, Alert } from '@mui/material';
import Layout from '../components/Layout';
import api from '../services/api';

const GC_PERMISSIONS = [
  { key: 'can_create_projects', label: 'Create Projects', description: 'GC can create new projects', category: 'Projects' },
  { key: 'can_view_budget', label: 'View Budget', description: 'GC can see budget tracker', category: 'Finance' },
  { key: 'can_view_homeowner_budget', label: 'View Homeowner Budget', description: 'GC can see full homeowner budget breakdown', category: 'Finance' },
  { key: 'can_approve_invoices', label: 'Approve Invoices', description: 'GC can approve subcontractor invoices', category: 'Finance' },
  { key: 'can_view_invoices', label: 'View Invoices', description: 'GC can see invoice list', category: 'Finance' },
  { key: 'can_create_contracts', label: 'Create Contracts', description: 'GC can upload and create contracts', category: 'Contracts' },
  { key: 'can_view_contracts', label: 'View Contracts', description: 'GC can see contracts', category: 'Contracts' },
  { key: 'can_view_vendors', label: 'View Vendors', description: 'GC can see vendor list', category: 'Vendors' },
  { key: 'can_invite_vendors', label: 'Invite Vendors', description: 'GC can add and invite vendors', category: 'Vendors' },
  { key: 'can_view_scopes', label: 'View Scopes', description: 'GC can see project scopes', category: 'Scopes' },
  { key: 'can_view_proposals', label: 'View Proposals', description: 'GC can see subcontractor proposals', category: 'Scopes' },
  { key: 'can_view_schedule', label: 'View Schedule', description: 'GC can see project schedule', category: 'Schedule' },
  { key: 'can_view_documents', label: 'View Documents', description: 'GC can see project documents', category: 'Documents' },
  { key: 'can_view_purchase_orders', label: 'View Purchase Orders', description: 'GC can see POs', category: 'Finance' },
];

const CATEGORIES = [...new Set(GC_PERMISSIONS.map(p => p.category))];

export default function GCSettings() {
  const [permissions, setPermissions] = useState({});
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get('/admin/settings').then(r => {
      setPermissions(r.data.gc_permissions || {});
    }).catch(() => {});
  }, []);

  const handleToggle = (key) => {
    setPermissions(prev => ({ ...prev, [key]: !prev[key] }));
    setSaved(false);
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await api.put('/admin/settings', { key: 'gc_permissions', value: permissions });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch(e) { alert('Failed to save'); }
    setLoading(false);
  };

  return (
    <Layout>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 800, color: '#1a237e' }}>GC Permissions</Typography>
        <Typography variant="body1" sx={{ color: '#666', mt: 1 }}>Control what General Contractors can see and do on the platform</Typography>
      </Box>

      {saved && <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>Settings saved successfully!</Alert>}

      {CATEGORIES.map(cat => (
        <Card key={cat} elevation={0} sx={{ border: '1px solid #e8eaf6', borderRadius: 3, mb: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 800, color: '#1a237e' }}>{cat}</Typography>
              <Chip label={`${GC_PERMISSIONS.filter(p => p.category===cat && permissions[p.key]).length}/${GC_PERMISSIONS.filter(p => p.category===cat).length} enabled`}
                size="small" sx={{ bgcolor: '#e8eaf6', color: '#1a237e', fontSize: 11 }} />
            </Box>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              {GC_PERMISSIONS.filter(p => p.category === cat).map(perm => (
                <Grid item xs={12} sm={6} key={perm.key}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    p: 2, bgcolor: permissions[perm.key] ? '#f0f4ff' : '#fafafa',
                    borderRadius: 2, border: `1px solid ${permissions[perm.key] ? '#c5cae9' : '#eee'}`,
                    transition: 'all 0.2s' }}>
                    <Box>
                      <Typography variant="body2" fontWeight={700}>{perm.label}</Typography>
                      <Typography variant="caption" color="text.secondary">{perm.description}</Typography>
                    </Box>
                    <Switch checked={permissions[perm.key] === true}
                      onChange={() => handleToggle(perm.key)} color="primary" size="small" />
                  </Box>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      ))}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
        <Button variant="contained" onClick={handleSave} disabled={loading}
          sx={{ bgcolor: '#1a237e', borderRadius: 2, px: 4, py: 1.5, fontWeight: 700, fontSize: 16 }}>
          {loading ? 'Saving...' : 'Save Settings'}
        </Button>
      </Box>
    </Layout>
  );
}
